import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Steps,
  Typography,
} from "antd";
import React, { useState, useEffect, useMemo } from "react";
import LilyButton from "../../../../components/button/lily-button";
import { useClaimContext } from "../component/claim-context";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  formatAddress,
  formatGender,
  formatPhone,
  parseDob,
} from "../../../../exports/util";
import { formatStateCode } from "../../../../exports/states-list";
import NumericInput from "../../../../components/input/input-number";
import CopyInfoComponent from "../../../../components/copay-info.component";

const PatientInformation = (props) => {
  const { setStage } = props;
  const user = useSelector((state) => state.authenticatedUser);
  const [form] = Form.useForm();
  const { Text } = Typography;
  // const [patientCurrent, setPatientCurrent] = useState(0);

  const {
    claimForm,
    setCurrent,
    setClaimForm,
    patientCurrent,
    setPatientCurrent,
  } = useClaimContext();

  const initialValues = {};

  const handleChangeInsuranceInfo = (e) => {
    const { name, value } = e.target;

    setClaimForm((prev) => ({
      ...prev,
      insuranceInfo: {
        ...prev.insuranceInfo,
        [name]: value,
      },
    }));
  };

  const handleSingleCheckboxChange = (groupName, value) => {
    setClaimForm((prev) => {
      const currentGroupValue = prev.insuranceInfo?.[groupName] ?? "";
      return {
        ...prev,
        insuranceInfo: {
          ...prev.insuranceInfo,
          [groupName]: currentGroupValue === value ? "" : value,
        },
      };
    });
  };

  const handleSubmit = () => {
    if (patientCurrent + 1 !== steps.length) {
      setPatientCurrent(patientCurrent + 1);
    } else {
      setCurrent(1);
    }
  };

  useEffect(() => {
    if (!user) return;

    form.setFieldsValue({
      fullName: user.fullName,
      dob: (user.dob && parseDob(user.dob).format("MM/DD/YYYY")) || null,
      email: user.email,
      phone: user.phone.number,
      line1: user.address.line1,
      line2: user.address.line2,
      city: user.address.city,
      state: formatStateCode(user.address.state),
      zip: user.address.zip,

      haveHealthInsurance: claimForm?.insuranceInfo?.haveHealthInsurance,
      prescriptionBenefits: claimForm?.insuranceInfo?.prescriptionBenefits,
      commercialHealthInsurance:
        claimForm?.insuranceInfo?.commercialHealthInsurance,
      insuranceCovered: claimForm?.insuranceInfo?.insuranceCovered,
      copayAmount: claimForm?.insuranceInfo?.copayAmount,
      prescriptionFilled: claimForm?.insuranceInfo?.prescriptionFilled,
    });
  }, [user, claimForm]);

  const filledDetails = (data) => {
    let fields = [
      {
        title: "Copay Card Member ID#",
        element: data?.memberId || "N/A",
      },
      {
        title: "First Name",
        element: data?.firstName || "N/A",
      },
      {
        title: "Last Name",
        element: data?.lastName || "N/A",
      },
      {
        title: "DOB",
        element: data?.dob ? parseDob(data?.dob).format("MM/DD/YYYY") : "N/A",
      },
      {
        title: "Gender",
        element: data?.gender ? formatGender[data.gender] : "N/A",
      },
      {
        title: "Address",
        element: (
          <Text>
            {formatAddress({
              line1: data?.address?.line1 || "",
              line2: data?.address?.line2 || "",
              city: data?.address?.city || "",
              state: data?.address?.state || "",
              zip: data?.address?.zip || "",
            })}
          </Text>
        ),
      },
      {
        title: "Phone",
        element: data?.phone?.number ? formatPhone(data?.phone?.number) : "N/A",
      },
      {
        className: "d-flex flex-column gap-2",
        title: "Copay Card Processing Information",
        element: <CopyInfoComponent data={data} />,
      },
      {
        className: "d-flex flex-row gap-2",
        title: "Participating Program",
        element: <Text>{data?.copayCardInfo?.programName || "N/A"}</Text>,
      },
    ];
    return fields;
  };

  const steps = [
    {
      key: 0,
      title: "Patient Information",
      content: "saving-card",
      element: (
        <div className="d-flex flex-column gap-1 px-3">
          {filledDetails(user).map((item) => {
            return (
              <div className={`${item.className || "d-flex flex-row gap-2"}`}>
                <Text>{item.title}:</Text>
                {item.element}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      key: 1,
      title: "Insurance Information",
      content: "insurance-information",
      element: (
        <div className="">
          <Divider className="cus-line" type="vertical" />
          <Form form={form} layout="vertical" initialValues={initialValues}>
            <Form.Item
              label="Do you have commercial/private health insurance?"
              name="haveHealthInsurance"
              rules={[{ required: true }]}
            >
              <Checkbox
                checked={
                  claimForm?.insuranceInfo?.haveHealthInsurance === "yes"
                }
                onChange={() =>
                  handleSingleCheckboxChange("haveHealthInsurance", "yes")
                }
              >
                Yes
              </Checkbox>
              <Checkbox
                checked={claimForm?.insuranceInfo?.haveHealthInsurance === "no"}
                onChange={() =>
                  handleSingleCheckboxChange("haveHealthInsurance", "no")
                }
              >
                No
              </Checkbox>
            </Form.Item>
            <Form.Item
              label="Prescription Drug Coverage Insurance Name"
              name="prescriptionBenefits"
              rules={[
                {
                  required: true,
                  message:
                    "Prescription Drug Coverage Insurance Name is required",
                },
              ]}
            >
              <Input
                name="prescriptionBenefits"
                onChange={handleChangeInsuranceInfo}
              />
            </Form.Item>
            <Form.Item
              label="Commercial/Private Health Insurance Name"
              name="commercialHealthInsurance"
              rules={[
                {
                  required: true,
                  message:
                    "Commercial/Private Health Insurance Name is required",
                },
              ]}
            >
              <Input
                name="commercialHealthInsurance"
                onChange={handleChangeInsuranceInfo}
              />
            </Form.Item>
            <Form.Item
              label="My insurance covered"
              name="insuranceCovered"
              rules={[{ required: true }]}
            >
              <div className="d-flex flex-column">
                <Checkbox
                  checked={
                    claimForm?.insuranceInfo?.insuranceCovered ===
                    "The entire prescription"
                  }
                  onChange={() =>
                    handleSingleCheckboxChange(
                      "insuranceCovered",
                      "The entire prescription",
                    )
                  }
                >
                  The entire prescription
                </Checkbox>
                <Checkbox
                  checked={
                    claimForm?.insuranceInfo?.insuranceCovered ===
                    "None of this prescription"
                  }
                  onChange={() =>
                    handleSingleCheckboxChange(
                      "insuranceCovered",
                      "None of this prescription",
                    )
                  }
                >
                  None of this prescription
                </Checkbox>
                <Checkbox
                  checked={
                    claimForm?.insuranceInfo?.insuranceCovered ===
                    "All except copay"
                  }
                  onChange={() =>
                    handleSingleCheckboxChange(
                      "insuranceCovered",
                      "All except copay",
                    )
                  }
                >
                  All except copay
                </Checkbox>
              </div>
            </Form.Item>
            <Form.Item
              label="How much was your copay?"
              name="copayAmount"
              rules={[
                { required: true, message: "Request Amount is required" },
              ]}
            >
              <NumericInput
                value={claimForm?.insuranceInfo?.copayAmount || ""}
                onChange={(value) => {
                  setClaimForm((prev) => ({
                    ...prev,
                    insuranceInfo: {
                      ...prev.insuranceInfo,
                      copayAmount: value,
                    },
                  }));
                }}
              />
            </Form.Item>
            <Form.Item
              label="This prescription was filled at"
              name="prescriptionFilled"
              rules={[{ required: true }]}
            >
              <div className="d-flex flex-column">
                <Checkbox
                  checked={
                    claimForm?.insuranceInfo?.prescriptionFilled ===
                    "A retail pharmacy store"
                  }
                  onChange={() =>
                    handleSingleCheckboxChange(
                      "prescriptionFilled",
                      "A retail pharmacy store",
                    )
                  }
                >
                  A retail pharmacy store
                </Checkbox>
                <Checkbox
                  checked={
                    claimForm?.insuranceInfo?.prescriptionFilled ===
                    "Through mail order or specialty pharmacy"
                  }
                  onChange={() =>
                    handleSingleCheckboxChange(
                      "prescriptionFilled",
                      "Through mail order or specialty pharmacy",
                    )
                  }
                >
                  Through mail order or specialty pharmacy
                </Checkbox>
              </div>
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ];

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
    description: <div>{patientCurrent === item.key && item.element}</div>,
  }));

  const disabledNext = useMemo(() => {
    if (patientCurrent === 1) {
      let values = claimForm.insuranceInfo;
      const requiredAreFilled = [
        "haveHealthInsurance",
        "insuranceCovered",
        "prescriptionFilled",
        "prescriptionBenefits",
        "commercialHealthInsurance",
        "copayAmount",
      ].every((key) => {
        const value = values?.[key];
        return (
          value !== undefined &&
          value !== null &&
          value.toString().trim() !== ""
        );
      });
      return !requiredAreFilled;
    }
    return false;
  }, [patientCurrent, claimForm]);

  return (
    <div style={{ minHeight: "300px" }}>
      {patientCurrent < steps.length && (
        <Steps
          className="steps-layout h-100"
          current={patientCurrent}
          items={items}
          direction="vertical"
        />
      )}
      <div className="d-flex flex-row justify-content-end mt-5">
        <Button
          style={{ width: "200px" }}
          color="default"
          variant="link"
          onClick={() => {
            if (patientCurrent === 0) {
              setStage("drug");
              return false;
            }
            if (patientCurrent - 1 !== steps.length) {
              setPatientCurrent(patientCurrent - 1);
            } else {
              setCurrent(1);
            }
          }}
        >
          Back
        </Button>
        <LilyButton
          disabled={disabledNext}
          style={{ width: "200px" }}
          onClick={handleSubmit}
          text="Next"
        />
      </div>
    </div>
  );
};

export default PatientInformation;
